// Turns the result of "Submit now" / "Send amendment" into one plain sentence
// for a toast or banner. Our wording only; the server has already mapped
// anything that came from Defra.

import type { DwtSubmitResult } from '../../api/dwt';

export interface ResultNote {
  tone: 'success' | 'error' | 'warning' | 'info';
  text: string;
}

function firstError(r: DwtSubmitResult): string | null {
  const e = r.messages.find((m) => m.severity === 'error');
  return e ? e.message : null;
}

export function describeSubmitResult(r: DwtSubmitResult, verb: 'report' | 'amendment' = 'report'): ResultNote {
  const noun = verb === 'amendment' ? 'The amendment' : 'The receipt';
  switch (r.result) {
    case 'submitted': {
      const warnings = r.messages.filter((m) => m.severity === 'warning').length;
      const id = r.waste_tracking_id ? ` Waste Tracking ID ${r.waste_tracking_id}.` : '';
      const w = warnings > 0 ? ` Defra accepted it with ${warnings} ${warnings === 1 ? 'warning' : 'warnings'} (see the receipt).` : '';
      return {
        tone: 'success',
        text: `${noun} was ${verb === 'amendment' ? 'sent and accepted by' : 'reported to'} Defra.${id}${w}`,
      };
    }
    case 'ineligible': {
      const first = r.staff_reasons[0];
      if (!first) return { tone: 'error', text: 'Not sent: this receipt cannot be reported yet.' };
      const more = r.staff_reasons.length > 1 ? ` (and ${r.staff_reasons.length - 1} more)` : '';
      return { tone: 'error', text: `Not sent: ${first.message} ${first.fix}${more}` };
    }
    case 'blocked_missing_data':
      return {
        tone: 'error',
        text: `Not sent: some information Defra needs is missing. ${firstError(r) ?? 'Open the receipt to see what to add.'}`,
      };
    case 'failed_permanent':
      return {
        tone: 'error',
        text: `Defra did not accept it. ${firstError(r) ?? 'Open the receipt to see why.'}`,
      };
    case 'retry_scheduled':
      return { tone: 'warning', text: 'Defra could not be reached or was busy. It will be tried again automatically.' };
    case 'unknown_outcome':
      return {
        tone: 'error',
        text: 'We could not confirm whether Defra received it. It will not be sent again until an admin checks Defra’s service and resolves it.',
      };
    case 'service_charge_unpaid':
      return {
        tone: 'error',
        text: 'Defra says the service charge is unpaid. Reporting is paused for your company until an admin clears the pause.',
      };
    case 'refused':
      return { tone: 'warning', text: 'A submission for this receipt is already in progress, or needs an admin to resolve it first.' };
    case 'not_amendable':
      return { tone: 'info', text: 'This receipt is not waiting for an amendment.' };
    case 'not_found':
      return { tone: 'error', text: 'Receipt not found.' };
  }
}
